// frontend/src/App.tsx
// =============================================================================
// HAUPTKOMPONENTE DER ANWENDUNG
// Hier läuft alles zusammen: Authentifizierung, State-Management,
// Projekt-Ladung, Polling und das grundlegende Layout (Admin-Panel & Plan).
// =============================================================================
import React, { useEffect, useRef, useState } from "react";
import Administration from "./Administration";
import Plan from "./Plan";

import { DataModel, defaultData } from "./AppTypes";
import { Mode, getSystemMode } from "./colorUtils";
import { applyPlanPatch, projectToDataModel } from "./projectToDataModel";

import { fetchCurrentUser, logoutBackend } from "./api/UserClient";
import { notifyProjectActivity } from "./api/pingClient";
import { ServiceUnavailableError } from "./api/baseClient";
import { LoginResponse, ProjectDetail, UserInfo } from "./model/backendTypes";

import { useAuth0 } from "@auth0/auth0-react";
import "./theme.css";

import LoginPage from "./LoginPage";

import { startPlanPolling } from "./api/PlanClient";
import { useIsMobile } from "./useIsMobile";
import BillingSuccessPage from "./BillingSuccessPage";


// Die zentrale App-Komponente.
export default function App() {
  // ----- State Definitionen -----
  // Theme-Modus (Hell/Dunkel), initial vom System abgeleitet.
  const [mode, setMode] = useState<Mode>(getSystemMode());

  // Hauptdaten-Objekt (Local State des geladenen Projekts)
  const [data, setData] = useState<DataModel | null>(null);
  // Aktueller Benutzer (vom Backend geladen)
  const [user, setUser] = useState<UserInfo | null>(null);

  // Das aktive Backend-Projekt (Metadaten wie ID, Name, Assessment)
  const [project, setProject] = useState<ProjectDetail | null>(null);

  // Funktion zum Stoppen des Pollings.
  // Wichtig: kein React-State, weil der Stopper synchron und closure-sicher verfügbar sein muss.
  const stopPollingRef = useRef<(() => void) | null>(null);

  const stopCurrentPolling = () => {
    if (stopPollingRef.current) {
      stopPollingRef.current();
      stopPollingRef.current = null;
    }
  };

  // UI-State: DB-Fehler (503)
  const [dbDown, setDbDown] = useState(false);

  // UI-State: Breite und Einklapp-Zustand des Admin-Panels
  const [adminWidth, setAdminWidth] = useState(260);
  const [collapsed, setCollapsed] = useState(true);

  // Forum panel disabled - now using link instead
  // const [forumWidth, setForumWidth] = useState(400);
  // const [forumCollapsed, setForumCollapsed] = useState(false);

  const minAdminWidth = 260;
  const maxAdminWidth = 450;
  // const minForumWidth = 260;
  // const maxForumWidth = 800;


  const isMobile = useIsMobile();
  const canAccessAdminPanel = user?.role === "admin" || user?.role === "owner";

  const {
    isAuthenticated,
    isLoading,
    logout,
    user: auth0User,
  } = useAuth0();

  // ----- Theme-Sync -----
  // Reagiert auf Änderungen des System-Farbschemas (Dark/Light Mode)
  // und passt den Modus in der App entsprechend an.
  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => setMode(getSystemMode());
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  // ----- Projekt-Logik -----
  // Zentrale Funktion: Wendet ein Projekt an, parst die enthaltenen Daten (XML/JSON)
  // und setzt den lokalen State.
  const applyProject = (proj: ProjectDetail | null) => {
    setProject(proj);

    if (!proj) {

      // DataModel leeren
      setData((prev) =>
        prev
          ? {
            ...prev,
            rules_xml: "",
            plan_csv: "",
            fulfillments_json: null,
            syntaxchecks_json: null,
            dimensions: [],
            timings: [],
            relations: [],
            conditions: [],
            conditionGroups: [],
            metrics: [],
          }
          : null
      );
      localStorage.removeItem("zg_project_id");
      return;
    }


    localStorage.setItem("zg_project_id", String(proj.id));

    try {
      setData(projectToDataModel(proj));
    } catch (e) {
      console.error("projectToDataModel failed:", e);
      // Fallback: keep app alive with minimal data
      setData({ ...defaultData, rules_xml: proj.rules_xml, plan_csv: proj.plan_csv ?? "" });
    }

    // Polling wird separat initiiert, siehe useEffect
    if (user && proj) {
      // Hinweis: Polling Aufruf könnte hier ergänzt werden, passiert aktuell aber
      // meist via useEffect oder handleProjectChange.
    }
    // WICHTIG: user.id muss verfügbar sein. Ggf. checken ob 'user' schon da ist.
    // Wenn diese Funktion aus syncBackendUser aufgerufen wird, ist 'user' State vllt noch alt?
    // -> Wir sollten die user-ID ggf. als Arg mitgeben oder sicherstellen dass user gesetzt ist.
    // Da wir in 'syncBackendUser' erst setUser aufrufen, ist es im selben Render-Cycle evtl. noch nicht da
    // wenn wir applyProject direkt danach rufen.
    // FIX: Wir übergeben userId optional oder greifen in syncBackendUser darauf zu.
    // Fürs erste nehmen wir 'user?.id' (State) oder verlassen uns drauf dass es passt. 
    // Aber cleaner: Wir starten Polling im useEffect, oder übergeben ID an applyProject.

    // Umbau: applyProject bekommt optional die userId mit, falls user-State noch nicht updated.
  };

  // ----- Polling -----
  const startPollingForProject = (uId: number, pId: number) => {
    if (!uId || !pId || uId <= 0 || pId <= 0) {
      console.warn("startPollingForProject: Ungültige User- oder Projekt-ID", { uId, pId });
      return;
    }

    stopCurrentPolling();

    const stop = startPlanPolling({
      userId: uId,
      projectId: pId,
      intervalMs: 2000,
      onFreshPlan: (planCsv, fulfillmentsJson, syntaxchecksJson, meta) => {
        setData((prev) => {
          if (!prev) return prev;
          return applyPlanPatch(prev, {
            plan_csv: planCsv,
            fulfillments_json: fulfillmentsJson,
            syntaxchecks_json: syntaxchecksJson,
          });
        });

        setProject((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            quality: meta.quality ?? prev.quality,
            improvement: meta.improvement ?? prev.improvement,
            scheduler_assessment:
              meta.scheduler_assessment ?? prev.scheduler_assessment,
            created_at: meta.created_at ?? prev.created_at,
            rules_at: meta.rules_at ?? prev.rules_at,
            solve_at: meta.solve_at ?? prev.solve_at,
            solve_estimate_at: meta.solve_estimate_at ?? prev.solve_estimate_at,
          };
        });
      },
    });

    stopPollingRef.current = stop;
  };

  useEffect(() => {
    return () => {
      stopCurrentPolling();
    };
  }, []);

  // ----- Initialisierung & Auth-Sync -----
  // Sobald Auth0 den User authentifiziert hat, laden wir die User-Daten vom eigenen Backend.
  // Dabei wird auch das letzte offene Projekt geladen.
  useEffect(() => {
    const syncBackendUser = async () => {
      // Wenn nicht eingeloggt über Auth0 → lokalen Zustand zurücksetzen
      if (!isAuthenticated) {
        stopCurrentPolling();
        setUser(null);
        applyProject(null);
        setData(defaultData);
        localStorage.removeItem("zg_project_id");
        return;
      }

      try {
        const resp: LoginResponse = await fetchCurrentUser();
        setUser(resp.user);

        const proj: ProjectDetail | null = resp.project ?? null;

        // Wichtig: Alte Projekt-IDs aus LocalStorage löschen, wenn kein aktives Projekt
        if (!proj) {
          localStorage.removeItem("zg_project_id");
        }

        applyProject(proj);

        // Polling direkt starten mit den gerade geholten IDs
        if (proj) {
          startPollingForProject(resp.user.id, proj.id);
        }

        localStorage.setItem("zg_user_name", resp.user.name);
      } catch (err) {
        if (err instanceof ServiceUnavailableError) {
          setDbDown(true);
        } else {
          console.error("Login/Sync mit Backend fehlgeschlagen:", err);
          stopCurrentPolling();
          setUser(null);
          applyProject(null);
          localStorage.removeItem("zg_project_id");
        }
      }
    };

    syncBackendUser();
  }, [isAuthenticated]);

  // Falls die Rolle keinen Admin-Zugriff hat, das Panel sicher einklappen.
  useEffect(() => {
    if (!canAccessAdminPanel) {
      setCollapsed(true);
    }
  }, [canAccessAdminPanel]);

  // ----- Logout -----
  // Meldet den User vom Backend und von Auth0 ab und bereinigt den lokalen State.
  const handleLogout = async () => {
    try {
      await logoutBackend();
    } catch (e) {
      console.warn("Backend logout failed:", e);
    }

    // Polling stoppen
    stopCurrentPolling();

    // Reset local frontend state
    setUser(null);
    applyProject(null);
    setData(defaultData);

    // LocalStorage bereinigen
    localStorage.removeItem("zg_project_id");
    localStorage.removeItem("zg_user_name");

    // Auth0-Logout
    logout({
      logoutParams: { returnTo: window.location.origin },
    });
  };

  // ----- UI Interaction -----
  // Startet das Dragging zum Ändern der Breite des Admin-Panels.
  const startDragging = (e: React.MouseEvent) => {
    if (isMobile) return; // Disable dragging on mobile
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = adminWidth;
    const onMouseMove = (me: MouseEvent) => {
      let w = startWidth + (me.clientX - startX);
      if (w < minAdminWidth) w = minAdminWidth;

      // Ensure we leave enough space for the rest of the app (Plan view)
      // We want to reserve at least 600px (or dynamic) for the rest
      const safeMax = window.innerWidth - 600;
      const effectiveMax = Math.min(maxAdminWidth, safeMax > minAdminWidth ? safeMax : minAdminWidth);

      if (w > effectiveMax) w = effectiveMax;
      setAdminWidth(w);
    };
    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  // Forum dragging disabled - forum panel is now a link
  /*
  const startForumDragging = (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = forumWidth;
    const onMouseMove = (me: MouseEvent) => {
      let w = startWidth - (me.clientX - startX);
      if (w < minForumWidth) w = minForumWidth;
      if (w > maxForumWidth) w = maxForumWidth;
      setForumWidth(w);
    };
    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };
  */



  // ----- Event Handler -----
  // Nur Projekt-Metadaten aktualisieren (z.B. solver_mode) ohne applyProject aufzurufen.
  // Verhindert, dass das DataModel neu geparst und das Polling gestoppt wird.
  const handleProjectMetaChange = (updatedProj: ProjectDetail) => {
    setProject(updatedProj);
  };

  // Wird aufgerufen, wenn im Admin-Bereich ein anderes Projekt geöffnet/erstellt wird.
  const handleProjectChange = (newProj: ProjectDetail | null) => {
    stopCurrentPolling();

    applyProject(newProj);

    if (user && newProj && newProj.id > 0) {
      startPollingForProject(user.id, newProj.id);
    }
  };

  /**
   * Wird verwendet, wenn sich im Setting-Panel der Organisationsname ändert.
   * Aktualisiert den globalen User-State, damit das Feld nicht "zurückspringt".
   */
  const handleUserChange = (newUser: UserInfo | null) => {
    setUser(newUser);
  };


  // ======== RENDER ========

  // 1. Ladezustand der Authentifizierung
  if (isLoading) {
    return <div style={{ minHeight: "100vh", background: "var(--bg)" }} />;
  }

  // 2. Nicht eingeloggt -> Login Seite anzeigen
  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // 2.5 Stripe success page
  // This app does not use React Router, so we route this page by checking the URL path.
  if (window.location.pathname === "/billing/success") {
    return <BillingSuccessPage />;
  }

  // Grid-Layout Berechnung basierend auf Mobile-View und Sidebar-Zustand
  let gridColumns = "";
  if (isMobile) {
    gridColumns = "1fr"; // Single column on mobile
  } else {
    // Desktop: Admin + Plan (+ Gutter)
    // Constraint: Admin never takes more than 40% of the screen width
    gridColumns = !collapsed ? `min(${adminWidth}px, 40vw) 6px minmax(0, 1fr)` : "minmax(0, 1fr)";
  }

  // 3. Haupt-Workspace Rendering
  // Besteht aus Admin-Panel (links), Trenner (resize) und Plan-Bereich (Mitte).
  return (
    <>
      <div
        className="layout"
        style={{
          gridTemplateColumns: gridColumns
        }}
      >
        {/* Linkes Panel: Administration */}
        {/* On mobile, we render it as an overlay if not collapsed. 
            On desktop, it's part of the grid if not collapsed. */}
        {canAccessAdminPanel && !collapsed && (
          <>
            {/* If mobile, throw a backdrop to close it */}
            {isMobile && (
              <div
                className="mobile-admin-backdrop"
                onClick={() => setCollapsed(true)}
              />
            )}
            <aside
              className={`pane pane--admin ${isMobile ? "pane--admin-mobile" : ""}`}
              onClick={() => { if (project?.id) notifyProjectActivity(project.id).catch(() => {}); }}
            >
              <Administration
                user={user}
                data={data}
                setData={setData}
                mode={mode}
                project={project}
                onProjectChange={handleProjectChange}
                onProjectMetaChange={handleProjectMetaChange}
                onUserChange={handleUserChange}
              />
              {/* Close button for mobile admin (optional, backdrop works too, but explicit X is nice) */}
              {isMobile && (
                <button
                  className="mobile-admin-close"
                  onClick={() => setCollapsed(true)}
                >
                  ◀
                </button>
              )}
            </aside>
          </>
        )}

        {/* Gutter: Only on desktop and if admin is open */}
        {canAccessAdminPanel && !collapsed && !isMobile && (
          <div
            className="gutter"
            onMouseDown={startDragging}
            onDoubleClick={() => setCollapsed(true)}
          />
        )}

        {/* Mitte: Plan */}
        <main className="pane pane--plan">
          <Plan
            data={data}
            mode={mode}
            user={user}
            onLogout={handleLogout}
            onToggleAdmin={canAccessAdminPanel ? () => setCollapsed((prev) => !prev) : undefined}
            adminCollapsed={collapsed}
            /*  onToggleForum={() => setForumCollapsed((prev) => !prev)}*/
            /*  forumCollapsed={forumCollapsed}*/
            projectName={project ? project.name : ""}
            projectId={project?.id}
            schedulerStatus={project?.scheduler_assessment ?? undefined}
            dbDown={dbDown}
            onProjectChange={handleProjectChange}
          />
        </main>

        {/* Forum panel disabled - now using link button in header instead */}
        {/*
        {!forumCollapsed && (
          <div
            className="gutter"
            onMouseDown={startForumDragging}
            onDoubleClick={() => setForumCollapsed(true)}
          />
        )}

        {!forumCollapsed && (
          <aside className="pane pane--forum">
            <iframe
              src="https://forum.zeit-geist.ai/"
              title="Forum"
              className="forum-frame"
            />
          </aside>
        )}
        */}


      </div>
    </>
  );
}
